// Transfer Orders drill-down — the legs behind a clicked card figure, grouped-by-lead in the
// response, shown here as one flat table with warehouse TABS built DYNAMICALLY from the legs.
//
// The tab dimension is whichever warehouse side actually varies for the direction: Bengaluru →
// Hyderabad has one destination (Hyderabad) but many Bengaluru SOURCES, so it tabs by origin
// warehouse; Hyderabad → Bengaluru has one origin (Hyderabad) but many Bengaluru DESTINATIONS, so it
// tabs by destination dock. origin_warehouse is dynamic (per leg), so the full list is fetched once
// (no server pagination) and filtered client-side by the active tab.
import { Button, Drawer, Table, Tabs, Tag, Tooltip } from "antd";
import { DownloadOutlined, WarningOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import FlagPills from "./flag_pills";
import { fmtIST } from "../../../oms/format";
import { downloadTransferOrdersPdf } from "./transfer_orders_pdf";
import {
  TransferDirection,
  TransferOrderRow,
  TransferStatusKey,
  listTransferOrders,
} from "../control_tower.api";
import {
  TRANSFER_DIRECTION_LABEL,
  TRANSFER_DOCK_LABEL,
  TRANSFER_STATUS_META,
  fmtTransferQty,
  fmtTransferWeight,
} from "../transfer_orders_meta";

// A clicked card figure. Tabs are derived from the fetched legs, not sent here.
export interface TransferDrilldownRequest {
  direction: TransferDirection;
  status?: TransferStatusKey; // a lifecycle bucket
  hasError?: boolean; // set when the card's error count was clicked (independent of status)
}

interface Props {
  request: TransferDrilldownRequest | null;
  dispatchFrom: string;
  dispatchTo: string;
  assignedTo?: string;
  onClose: () => void;
}

const ALL_TAB = "all";

const dash = <span style={{ color: "#bbb" }}>—</span>;

// due_at is an ISO timestamp — show the date + time (IST clock, no tz maths, matching the board).
const fmtDateTime = (v: string | null) => (v ? dayjs(v).format("DD MMM YYYY, HH:mm") : "—");

// A weight in kg, up to 2 decimals, or "—" when unmapped. Coerces numeric strings too — Django
// DecimalField serialises decimals as strings ("52.92").
const fmtKg = (v: number | string | null | undefined) => {
  const n = typeof v === "number" ? v : v != null && v !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? `${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg` : "—";
};

// A leg's weight: the server's total when present, else the sum of its cargo lines.
const legWeight = (r: TransferOrderRow): number | string | null => {
  if (r.cargo_weight_kg != null && r.cargo_weight_kg !== "") return r.cargo_weight_kg;
  if (r.cargo_lines?.length)
    return r.cargo_lines.reduce((sum, l) => sum + (Number(l.weight_kg) || 0), 0);
  return null;
};

// --- Warehouse tab dimension: origin (sending) vs dock (receiving) --------------------------------
type TabField = "origin" | "dock";

const originLabel = (r: TransferOrderRow): string =>
  r.origin_warehouse || (r.origin_warehouse_id != null ? `#${r.origin_warehouse_id}` : "—");
const originKey = (r: TransferOrderRow): string => String(r.origin_warehouse_id ?? r.origin_warehouse ?? "unknown");

const tabKeyOf = (r: TransferOrderRow, field: TabField): string => (field === "origin" ? originKey(r) : r.dock);
const tabLabelOf = (r: TransferOrderRow, field: TabField): string =>
  field === "origin" ? originLabel(r) : TRANSFER_DOCK_LABEL[r.dock] ?? r.dock;

const legColumns: ColumnsType<TransferOrderRow> = [
  {
    title: "PO Number",
    dataIndex: "po_number",
    key: "po_number",
    render: (v: string | null, r) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span>{v ?? "—"}</span>
        <FlagPills express={r.express} unloading={r.unloading} />
      </div>
    ),
  },
  {
    title: "Lead ID",
    key: "lead_id",
    render: (_, r) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span>{r.lead_id ?? "—"}</span>
        {r.oms_sales_order_id ? (
          <a
            href={`/oms/${r.oms_sales_order_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12 }}
          >
            SO #{r.oms_sales_order_id}
          </a>
        ) : null}
      </div>
    ),
  },
  {
    // The SOURCE (sending) warehouse — dynamic name, fall back to the id, then "—".
    title: "Origin Warehouse",
    key: "origin_warehouse",
    render: (_, r) => r.origin_warehouse || (r.origin_warehouse_id != null ? `#${r.origin_warehouse_id}` : dash),
  },
  {
    // The RECEIVING (destination) warehouse.
    title: "Receiving Warehouse",
    key: "dock",
    render: (_, r) => TRANSFER_DOCK_LABEL[r.dock] ?? r.dock ?? dash,
  },
  { title: "Supplier", dataIndex: "supplier", key: "supplier", render: (v) => v ?? dash },
  {
    // Physical progress — the lifecycle chip. Never red (the blocker/error is a separate column).
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (v: TransferStatusKey) => {
      const meta = TRANSFER_STATUS_META[v];
      return meta ? <Tag color={meta.color}>{meta.label}</Tag> : dash;
    },
  },
  {
    // Source-warehouse GRN state (goods left the source yet?), plus a genuine failure/hold badge.
    title: "Source GRN",
    key: "source_grn",
    render: (_, r) => {
      const grn = r.previous_warehouse_grn_marked;
      const grnTag =
        grn === false ? (
          <Tag color="orange" style={{ margin: 0 }}>
            Awaiting GRN at source
          </Tag>
        ) : grn === true ? (
          <Tag color="green" style={{ margin: 0 }}>
            GRN Marked
          </Tag>
        ) : null;
      const errTag = r.has_error ? (
        <Tooltip title={r.error_message || r.hold_reason || r.error || undefined} overlayStyle={{ maxWidth: 480 }}>
          <Tag color="error" icon={<WarningOutlined />} style={{ margin: 0, cursor: "help" }}>
            {r.error || "Stuck"}
          </Tag>
        </Tooltip>
      ) : null;
      if (!grnTag && !errTag) return dash;
      return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
          {grnTag}
          {errTag}
        </div>
      );
    },
  },
  {
    title: "Due",
    dataIndex: "due_at",
    key: "due_at",
    width: 160,
    render: (v: string | null) => <span style={{ whiteSpace: "nowrap" }}>{fmtDateTime(v)}</span>,
  },
  {
    // Aging of the transfer — when the order was placed and when this allocation was created (IST).
    title: "Ordered / Allocated",
    key: "aging",
    render: (_, r) => (
      <div style={{ fontSize: 12, lineHeight: 1.4, whiteSpace: "nowrap" }}>
        <div>Ordered {fmtIST(r.order_placed_time, "DD MMM")}</div>
        <div style={{ color: "#8c8c8c" }}>Allocated {fmtIST(r.allocation_created_time, "DD MMM")}</div>
      </div>
    ),
  },
  {
    title: "Qty",
    dataIndex: "total_quantity",
    key: "total_quantity",
    align: "right",
    render: (v) => fmtTransferQty(v),
  },
  {
    title: "Weight",
    key: "cargo_weight_kg",
    align: "right",
    render: (_, r) => fmtKg(legWeight(r)),
  },
  {
    // Line items shown inline in a fixed-width scrollable cell: item name, then its quantity in bold.
    title: "Items",
    key: "items",
    width: 260,
    render: (_, r) =>
      r.cargo_lines?.length ? (
        <div style={{ maxHeight: 88, overflow: "auto", fontSize: 12, lineHeight: 1.5, paddingRight: 4 }}>
          {r.cargo_lines.map((l) => (
            <div key={l.variant_id} style={{ whiteSpace: "nowrap" }}>
              {l.name} × <b>{l.qty}</b>
            </div>
          ))}
        </div>
      ) : (
        dash
      ),
  },
];

const titleFor = (request: TransferDrilldownRequest): string =>
  [
    TRANSFER_DIRECTION_LABEL[request.direction],
    request.hasError
      ? "Stuck"
      : request.status
        ? TRANSFER_STATUS_META[request.status].label
        : "Total Shipments",
  ].join(" · ");

const TransferOrdersDrawer: React.FC<Props> = ({ request, dispatchFrom, dispatchTo, assignedTo, onClose }) => {
  // The FULL, unpaginated list of legs for the current filters (transfers are bounded, so we fetch
  // all and slice into tabs client-side).
  const [legsAll, setLegsAll] = useState<TransferOrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);
  const [downloading, setDownloading] = useState(false);

  // Reset to the All tab on a new drill-down.
  useEffect(() => {
    setActiveTab(ALL_TAB);
  }, [request]);

  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    // No page/page_size → the backend returns the full list; the whole direction (no dock filter),
    // so every origin/dock is present to build the tabs from.
    listTransferOrders({
      dispatch_from: dispatchFrom,
      dispatch_to: dispatchTo,
      assigned_to: assignedTo,
      direction: request.direction,
      status: request.status,
      has_error: request.hasError,
    }).then((res) => {
      if (cancelled) return;
      setLegsAll((res?.results ?? []).flatMap((g) => g.transfers ?? []));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [request, dispatchFrom, dispatchTo, assignedTo]);

  // Tab by whichever warehouse side varies: origin when there are multiple sources, else dock when
  // there are multiple destinations, else no tabs.
  const tabField: TabField | null = useMemo(() => {
    const origins = new Set(legsAll.map(originKey));
    if (origins.size > 1) return "origin";
    const docks = new Set(legsAll.map((l) => l.dock));
    if (docks.size > 1) return "dock";
    return null;
  }, [legsAll]);

  // The distinct tab values (key + label + leg count), sorted by label.
  const tabOptions = useMemo(() => {
    if (!tabField) return [];
    const map = new Map<string, { label: string; count: number }>();
    legsAll.forEach((l) => {
      const key = tabKeyOf(l, tabField);
      const entry = map.get(key) ?? { label: tabLabelOf(l, tabField), count: 0 };
      entry.count += 1;
      map.set(key, entry);
    });
    return Array.from(map, ([key, v]) => ({ key, label: v.label, count: v.count })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [legsAll, tabField]);

  // The legs shown for the active tab.
  const legs = useMemo(() => {
    if (!tabField || activeTab === ALL_TAB) return legsAll;
    return legsAll.filter((l) => tabKeyOf(l, tabField) === activeTab);
  }, [legsAll, tabField, activeTab]);

  const totalWeight = legs.reduce((s, l) => s + (Number(legWeight(l)) || 0), 0);
  const totalQty = legs.reduce((s, l) => s + (Number(l.total_quantity) || 0), 0);
  const leadCount = new Set(legs.map((l) => l.lead_id ?? `so-${l.oms_sales_order_id}`)).size;

  const tabItems = tabField
    ? [
        { key: ALL_TAB, label: `All (${legsAll.length})` },
        ...tabOptions.map((o) => ({ key: o.key, label: `${o.label} (${o.count})` })),
      ]
    : [];

  // Download the CURRENT view (active tab) as a receiving checklist PDF: group the shown legs by
  // lead, drop already-received (transferred) legs, keep errored ones.
  const handleDownload = async () => {
    if (!request) return;
    setDownloading(true);
    const byLead = new Map<
      string,
      { lead_id: string | null; oms_sales_order_id: number | null; legs: TransferOrderRow[] }
    >();
    legs
      .filter((l) => l.status !== "transferred")
      .forEach((l) => {
        const key = l.lead_id ?? `so-${l.oms_sales_order_id ?? "?"}`;
        const g =
          byLead.get(key) ?? { lead_id: l.lead_id, oms_sales_order_id: l.oms_sales_order_id, legs: [] };
        g.legs.push(l);
        byLead.set(key, g);
      });
    // Scope the sheet to the active warehouse tab; the "All" view prints no warehouse line.
    const tabLabel = activeTab !== ALL_TAB ? tabOptions.find((o) => o.key === activeTab)?.label : undefined;
    downloadTransferOrdersPdf(Array.from(byLead.values()), {
      warehouse: tabLabel
        ? { label: tabField === "origin" ? "Origin Warehouse" : "Receiving Warehouse", value: tabLabel }
        : undefined,
      dispatchFrom,
      dispatchTo,
    });
    setDownloading(false);
  };

  return (
    <Drawer
      title={request ? titleFor(request) : ""}
      width={1080}
      open={!!request}
      onClose={onClose}
      destroyOnClose
      extra={
        request ? (
          <Button icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>
            Download PDF
          </Button>
        ) : undefined
      }
    >
      {/* Totals for the active tab: weight + quantity + the leg/lead counts. */}
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontWeight: 600 }}>
          Total weight: <span style={{ color: "#0958d9" }}>{fmtTransferWeight(totalWeight)}</span>
        </span>
        {"  ·  "}
        <span style={{ fontWeight: 600 }}>
          Qty <span style={{ color: "#0958d9" }}>{fmtTransferQty(totalQty)}</span>
        </span>
        {"  ·  "}
        <span style={{ color: "#8c8c8c" }}>
          {legs.length} leg{legs.length === 1 ? "" : "s"} in {leadCount} lead{leadCount === 1 ? "" : "s"}
        </span>
      </div>
      {tabField && <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />}
      <Table<TransferOrderRow>
        rowKey="id"
        size="small"
        loading={loading}
        columns={legColumns}
        dataSource={legs}
        pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
        scroll={{ x: 1200 }}
      />
    </Drawer>
  );
};

export default TransferOrdersDrawer;
